import React, { useState, useMemo } from 'react';
import { GameState, FreeAgent, TeamInLeague } from '../../../domain/entities';
import { PlayerFace } from '../../shared/PlayerFace';
import { useToast } from '../../../contexts/ToastContext';
import { soundService } from '../../../services/SoundService';
import { View } from '../../../App';
import { PotentialBadge } from '../../shared/PotentialBadge';
import { Modal } from '../../shared/Modal';
import { MyActiveBids } from './MyActiveBids';
import { useTableFilters } from '../../../hooks/useTableFilters';
import { TableHeader } from '../../shared/TableHeader';
import { AdvancedFilterModal } from '../../shared/AdvancedFilterModal';

interface FreeAgentsProps {
    gameState: GameState;
    playerTeam: TeamInLeague;
    onSubmitFreeAgentBid: (freeAgentId: number, wage: number) => Promise<{ success: boolean; message: string; }>;
    pushView: (view: View) => void;
}

export const FreeAgents: React.FC<FreeAgentsProps> = ({
    gameState,
    playerTeam,
    onSubmitFreeAgentBid,
    pushView
}) => {
    const { addToast } = useToast();
    const [negotiatingPlayer, setNegotiatingPlayer] = useState<FreeAgent | null>(null);
    const [negotiationStep, setNegotiationStep] = useState<'initial' | 'player_terms' | 'complete'>('initial');
    const [selectedWageMultiplier, setSelectedWageMultiplier] = useState(1.0);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 50;

    // Use centralized filter management with localStorage persistence
    const {
        filters: tableFilters,
        updateFilter,
        clearFilters,
        isFilterModalOpen,
        setIsFilterModalOpen,
        hasActiveFilters
    } = useTableFilters('free-agents', {
        search: '',
        sortBy: 'skill_desc',
        positionFilter: 'ALL',
        minSkill: 0,
        maxSkill: 120,
        minAge: 16,
        maxAge: 40,
        minBids: 0,
    });

    // Get unique positions
    const positions = useMemo(() => {
        const pos = new Set<string>((gameState.freeAgents || []).map(fa => fa.position));
        return Array.from(pos).sort();
    }, [gameState.freeAgents]);

    const filteredAndSortedAgents = useMemo(() => {
        let agents = [...(gameState.freeAgents || [])];

        // Apply text search
        agents = agents.filter(a =>
            a.playerName.toLowerCase().includes(tableFilters.search.toLowerCase())
        );

        // Apply filters
        if (tableFilters.positionFilter !== 'ALL') {
            agents = agents.filter(a => a.position === tableFilters.positionFilter);
        }
        agents = agents.filter(a =>
            a.skill >= tableFilters.minSkill &&
            a.skill <= tableFilters.maxSkill &&
            a.age >= tableFilters.minAge &&
            a.age <= tableFilters.maxAge &&
            a.totalBids >= tableFilters.minBids
        );

        // Apply sorting
        agents.sort((a, b) => {
            switch (tableFilters.sortBy) {
                case 'skill_asc': return a.skill - b.skill;
                case 'potential_desc': return b.potential - a.potential;
                case 'potential_asc': return a.potential - b.potential;
                case 'age_asc': return a.age - b.age;
                case 'age_desc': return b.age - a.age;
                case 'name_asc': return a.playerName.localeCompare(b.playerName);
                case 'bids_desc': return b.totalBids - a.totalBids;
                case 'skill_desc':
                default: return b.skill - a.skill;
            }
        });

        return agents;
    }, [gameState.freeAgents, tableFilters]);

    const totalPages = Math.ceil(filteredAndSortedAgents.length / ITEMS_PER_PAGE);
    const paginatedAgents = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredAndSortedAgents.slice(startIndex, endIndex);
    }, [filteredAndSortedAgents, currentPage]);

    const getDeadlineString = (freeAgent: FreeAgent): string => {
        const deadline = freeAgent.bidDeadline;
        if (!deadline) {
            return 'No deadline';
        }
        return `S${deadline.season}W${deadline.week}`;
    };

    const getHighestBid = (freeAgent: FreeAgent): number => {
        let maxWage = 0;
        if (freeAgent.offers && freeAgent.offers.length > 0) {
            maxWage = Math.max(...freeAgent.offers.map(o => o.wage), maxWage);
        }
        if (freeAgent.userBid) {
            maxWage = Math.max(maxWage, freeAgent.userBid.wage);
        }
        return maxWage;
    };

    const handleSignPlayer = async () => {
        if (!negotiatingPlayer) return;

        setIsSubmitting(true);
        setMessage(null);

        try {
            // Submit the bid through the proper game action
            const baseWage = negotiatingPlayer.wage || Math.max(1000, Math.floor(negotiatingPlayer.skill * 100));
            const offeredWage = Math.floor(baseWage * selectedWageMultiplier);

            const result = await onSubmitFreeAgentBid(negotiatingPlayer.playerId, offeredWage);

            if (result.success) {
                setMessage({ type: 'success', text: result.message });
                soundService.playSuccess();

                // Close the modal after showing success
                setTimeout(() => {
                    setNegotiatingPlayer(null);
                    setNegotiationStep('initial');
                    setSelectedWageMultiplier(1.0);
                    setMessage(null);
                }, 2000);
            } else {
                setMessage({ type: 'error', text: result.message });
                soundService.playError();
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Failed to submit bid: ' + String(error) });
            soundService.playError();
        } finally {
            setIsSubmitting(false);
        }
    };

    if ((gameState.freeAgents || []).length === 0) {
        return (
            <div className="panel">
                <div className="panel-header">Free Agents</div>
                <div className="panel-content" style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    <p>No free agents available in the transfer market.</p>
                </div>
            </div>
        );
    }

    // Reset page to 1 when filters or sort change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [tableFilters]);

    const handleFilterChange = (key: string, value: any) => {
        updateFilter(key, value);
    };

    const handleClearFilters = () => {
        clearFilters();
    };

    return (
        <>
            {/* My Active Bids Section */}
            <MyActiveBids gameState={gameState} pushView={pushView} />

            {/* Table Controls */}
            <TableHeader
                searchValue={tableFilters.search}
                onSearchChange={(value) => handleFilterChange('search', value)}
                searchPlaceholder="Search player names..."
                sortValue={tableFilters.sortBy}
                onSortChange={(value) => handleFilterChange('sortBy', value)}
                sortOptions={[
                    { label: 'Skill (High to Low)', value: 'skill_desc' },
                    { label: 'Skill (Low to High)', value: 'skill_asc' },
                    { label: 'Potential (High to Low)', value: 'potential_desc' },
                    { label: 'Potential (Low to High)', value: 'potential_asc' },
                    { label: 'Age (Young to Old)', value: 'age_asc' },
                    { label: 'Age (Old to Young)', value: 'age_desc' },
                    { label: 'Bids (Most to Least)', value: 'bids_desc' },
                    { label: 'Name (A to Z)', value: 'name_asc' },
                ]}
                onFilterClick={() => setIsFilterModalOpen(true)}
                onClearFilters={handleClearFilters}
                hasActiveFilters={hasActiveFilters}
            />

            {/* Advanced Filter Modal */}
            <AdvancedFilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                filters={tableFilters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                filterOptions={[
                    {
                        label: 'Position',
                        type: 'select',
                        key: 'positionFilter',
                        options: [
                            { label: 'All Positions', value: 'ALL' },
                            ...positions.map(pos => ({ label: pos, value: pos })),
                        ]
                    },
                    { label: 'Min Skill', type: 'range', key: 'minSkill', min: 0, max: 120, step: 1 },
                    { label: 'Max Skill', type: 'range', key: 'maxSkill', min: 0, max: 120, step: 1 },
                    { label: 'Min Age', type: 'range', key: 'minAge', min: 16, max: 40, step: 1 },
                    { label: 'Max Age', type: 'range', key: 'maxAge', min: 16, max: 40, step: 1 },
                    { label: 'Min Bids', type: 'range', key: 'minBids', min: 0, max: 10, step: 1 },
                ]}
                title="Free Agent Filters"
            />

            {filteredAndSortedAgents.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    <p>No free agents match your filters.</p>
                </div>
            ) : (
                <>
                    <div style={{ marginBottom: '16px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                        Showing {paginatedAgents.length} of {filteredAndSortedAgents.length} free agents
                    </div>

                    <div className="table-wrapper">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Player</th>
                                    <th>Age</th>
                                    <th>Pos</th>
                                    <th>Skill</th>
                                    <th># Bids</th>
                                    <th>Highest Bid</th>
                                    <th>Deadline</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedAgents.map((agent) => {
                                    const highestBid = getHighestBid(agent);
                                    const deadline = getDeadlineString(agent);
                                    return (
                                        <tr key={agent.playerId}>
                                            <td style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <PlayerFace player={{ hue: agent.hue, faceEmoji: agent.faceEmoji, isLegend: false } as any} />
                                                <button
                                                    className="link-style"
                                                    onClick={() => pushView({ type: 'playerDetail', title: 'Player Detail', playerId: agent.playerId })}
                                                >
                                                    {agent.playerName}
                                                </button>
                                            </td>
                                            <td>{agent.age}</td>
                                            <td>{agent.position}</td>
                                            <td>{agent.skill}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{agent.totalBids}</td>
                                            <td>{highestBid > 0 ? `£${highestBid.toLocaleString()}` : '—'}</td>
                                            <td style={{ fontSize: '0.9em', opacity: 0.8 }}>{deadline}</td>
                                            <td>
                                                <button
                                                    className="button small"
                                                    onClick={() => {
                                                        soundService.playClick();
                                                        setNegotiatingPlayer(agent);
                                                        setNegotiationStep('player_terms');
                                                    }}
                                                >
                                                    Bid
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
                    <button
                        className="button small"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                    >
                        ← Prev
                    </button>
                    <span style={{ fontSize: '0.9em', opacity: 0.8 }}>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        className="button small"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                    >
                        Next →
                    </button>
                </div>
            )}

            {negotiatingPlayer && negotiationStep === 'player_terms' && (
                <Modal isOpen={true} onClose={() => {
                    if (!isSubmitting) {
                        setNegotiatingPlayer(null);
                        setNegotiationStep('initial');
                        setSelectedWageMultiplier(1.0);
                        setMessage(null);
                    }
                }}>
                    <h2>Place Bid - {negotiatingPlayer.playerName}</h2>
                    {message && <p className={message.type === 'success' ? 'positive' : 'negative'}>{message.text}</p>}

                    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'rgba(var(--accent-color-rgb), 0.1)', borderRadius: '4px', fontSize: '0.9em' }}>
                        <p><strong>Position:</strong> {negotiatingPlayer.position} | <strong>Skill:</strong> {negotiatingPlayer.skill}</p>
                        <p style={{ margin: '8px 0 0 0', opacity: 0.8 }}>
                            {negotiatingPlayer.bidDeadline
                                ? `This player will decide between all bids by Season ${negotiatingPlayer.bidDeadline.season} Week ${negotiatingPlayer.bidDeadline.week}.`
                                : `This player is waiting for their first bid. Once you or another team bids, they will decide by 2 weeks from that date.`
                            }
                            {' '}They currently have {negotiatingPlayer.totalBids} bid(s).
                        </p>
                    </div>

                    <p>Select a weekly wage offer:</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                        {[0.8, 0.9, 1.0, 1.1, 1.2].map((multiplier) => {
                            const baseWage = negotiatingPlayer.wage || Math.max(1000, Math.floor(negotiatingPlayer.skill * 100));
                            const wage = Math.floor(baseWage * multiplier);
                            const label = multiplier === 1.0 ? 'Suggested' : multiplier < 1.0 ? `${Math.round((1 - multiplier) * 100)}% Below` : `${Math.round((multiplier - 1) * 100)}% Above`;
                            return (
                                <button
                                    key={multiplier}
                                    className={`button ${selectedWageMultiplier === multiplier ? 'primary' : ''}`}
                                    onClick={() => setSelectedWageMultiplier(multiplier)}
                                    style={{ textAlign: 'left', padding: '12px' }}
                                >
                                    <span>£{wage.toLocaleString()}/week</span>
                                    <span style={{ marginLeft: '12px', opacity: 0.7, fontSize: '0.9em' }}>({label})</span>
                                </button>
                            );
                        })}
                    </div>

                    <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderLeft: '3px solid var(--accent-color)', marginBottom: '16px', fontSize: '0.9em' }}>
                        <p style={{ margin: 0 }}>
                            <strong>Annual cost:</strong> £{(Math.floor((negotiatingPlayer.wage || Math.max(1000, Math.floor(negotiatingPlayer.skill * 100))) * selectedWageMultiplier) * 52).toLocaleString()}
                        </p>
                    </div>

                    <div className="modal-actions">
                        <button className="button" onClick={() => {
                            setNegotiatingPlayer(null);
                            setNegotiationStep('initial');
                            setSelectedWageMultiplier(1.0);
                            setMessage(null);
                        }} disabled={isSubmitting}>
                            Cancel
                        </button>
                        <button className="button primary" onClick={handleSignPlayer} disabled={isSubmitting}>
                            {isSubmitting ? 'Submitting Bid...' : 'Submit Bid'}
                        </button>
                    </div>
                </Modal>
            )}
        </>
    );
};
